import config from '../config'
import { loadRemote } from '@module-federation/runtime'
import store from '../store'
// Util function to mount and unmount the appopintment modal based on the type
import { useAppContext } from '../pmd/composition/useAppContext'
import { getJWT } from './jwt_helper'
import { getCalendarData } from './calendar_modal'
import { defineAsyncMicroApp } from '@gohighlevel/ghl-module-federation-plugin'
import { ref } from 'vue'

let calendarIntegrationApp: any = {}
let troubleshootCalendarApp: any = {}
let reconnectModal: any = {}
let serviceSettingsApp: any = {}
let calendarScheduleApp: any = {}
let removeDeletedCalendarsApp: any = {}
let whatsappTemplateCreatorApp: any = {}

// Function to mount a modal component
async function mountCalendarSettingsApp(mountId: string, configuration: any) {
  const globallyMountedApps = ['ReconnectModal', 'RemoveDeletedCalendars'] // If the app is globally mounted, we don't need to unmount other apps while mounting this app
  if (!globallyMountedApps.includes(mountId)) {
    if (calendarIntegrationApp?.unmount) calendarIntegrationApp?.unmount()
    if (troubleshootCalendarApp?.unmount) troubleshootCalendarApp?.unmount()
    if (serviceSettingsApp?.unmount) serviceSettingsApp?.unmount()
    if (calendarScheduleApp?.unmount) calendarScheduleApp?.unmount()
  }

  const { mountAppContext } = useAppContext()
  let Component
  switch (mountId) {
    case 'CalendarIntegration':
      Component = 'CalendarIntegration'
      break
    case 'TroubleshootCalendar':
      Component = 'TroubleshootCalendar'
      break
    case 'ReconnectModal':
      Component = 'ReconnectModal'
      break
    case 'ServiceSettings':
      Component = 'ServiceSettings'
      break
    case 'CalendarSchedule':
      Component = 'CalendarSchedule'
      break
    case 'RemoveDeletedCalendars':
      Component = 'RemoveDeletedCalendars'
      break
    default:
      Component = null
  }

  try {
    const [createApp, calendarComponent] = await Promise.all([
      loadRemote('calendarSettingsApp/vue3').then((m: any) => m.createApp),
      loadRemote('calendarSettingsApp/' + Component).then((m: any) => m.default),
    ])
    const calendarSettingsApp = createApp(calendarComponent, {
      ...configuration,
    })
    mountAppContext({ calendarSettingsApp })
    calendarSettingsApp.mount(`#${mountId}`)
    if (mountId === 'CalendarIntegration') {
      calendarIntegrationApp = calendarSettingsApp
    } else if (mountId === 'TroubleshootCalendar') {
      troubleshootCalendarApp = calendarSettingsApp
    } else if (mountId === 'ServiceSettings') {
      serviceSettingsApp = calendarSettingsApp
    } else if (mountId === 'CalendarSchedule') {
      calendarScheduleApp = calendarSettingsApp
    } else if (mountId === 'RemoveDeletedCalendars') {
      removeDeletedCalendarsApp = calendarSettingsApp
    } else if (mountId === 'ReconnectModal') {
      reconnectModal = calendarSettingsApp
    }
  } catch (e) {
    console.error(e)
  }
}

// Function to unmount a modal component
function unmountCalendarSettingsApp(mountId: string) {
  let appToUnmount

  switch (mountId) {
    case 'CalendarIntegration':
      appToUnmount = calendarIntegrationApp
      break
    case 'TroubleshootCalendar':
      appToUnmount = troubleshootCalendarApp
      break
    case 'ReconnectModal':
      appToUnmount = reconnectModal
      break
    case 'ServiceSettings':
      appToUnmount = serviceSettingsApp
      break
    case 'CalendarSchedule':
      appToUnmount = calendarScheduleApp
      break
    case 'RemoveDeletedCalendars':
      appToUnmount = removeDeletedCalendarsApp
      break
  }
  if (appToUnmount?.unmount) {
    try {
      const mountElement = document.getElementById(mountId)
      if (mountElement) {
        appToUnmount.unmount()
      }
    } catch (error) {
    } finally {
      window['calendarSettingsApp'] = null
    }
  }
}

async function mountWhatsappTemplateCreatorApp(
  store: any,
  configuration: any,
  whatsappTemplateCreatorPostSaveHandler: any
) {
  try {
    const [createApp, CreateTemplateExporter] = await defineAsyncMicroApp('phoneIntegrationApp', [
      'vue3',
      'CreateTemplateExporter',
    ])

    whatsappTemplateCreatorApp = createApp(CreateTemplateExporter, {
      store: store,
      locationId: configuration.locationId,
      templateId: configuration.templateId || null,
      source: 'calendar_notification',
      postSaveHandler: whatsappTemplateCreatorPostSaveHandler,
      action: configuration.isEdit ? 'edit' : null,
    })

    const { mountAppContext } = useAppContext()
    window['whatsAppSettingsApp'] = whatsappTemplateCreatorApp
    mountAppContext({ whatsappTemplateCreatorApp })

    const mountTarget = '#calendar-notification-whatsapp-template-creator'

    whatsappTemplateCreatorApp.mount(mountTarget)

    // Set up DOM event listener to intercept "Back to Templates" button clicks
    setTimeout(() => {
      const handleBackButtonClick = (event: Event) => {
        const target = event.target as HTMLElement
        const isBackButton = target && target.textContent?.toLowerCase().includes('back to templates')

        if (isBackButton) {
          event.preventDefault()
          event.stopPropagation()

          // Try multiple ways to close the modal
          // Method 1: Find the close function through window
          if (window.closeWhatsappTemplateCreator) {
            window.closeWhatsappTemplateCreator()
            return
          }

          // Method 2: Trigger close via clicking the modal backdrop
          const modalBackdrop = document.querySelector('[data-whatsapp-modal-close]')
          if (modalBackdrop) {
            ;(modalBackdrop as HTMLElement).click()
            return
          }

          // Method 3: Find close button and click it
          const closeButton = document.querySelector('button[title*="Close WhatsApp"]') as HTMLElement
          if (closeButton) {
            closeButton.click()
            return
          }
        }
      }

      // Add event listener to the document body to catch all clicks
      document.body.addEventListener('click', handleBackButtonClick, true)
      // Store the handler for cleanup
      window['whatsappTemplateBackButtonHandler'] = handleBackButtonClick
    }, 1000) // Give time for the component to fully mount
  } catch (error) {
    console.log('Error mounting WhatsApp Template Creator App:', error)
  }
}

function unmountWhatsappTemplateCreatorApp() {
  // Clean up event listener
  const handler = window['whatsappTemplateBackButtonHandler']
  if (handler) {
    document.body.removeEventListener('click', handler, true)
    delete window['whatsappTemplateBackButtonHandler']
  }

  // Clean up window reference
  if (window.closeWhatsappTemplateCreator) {
    delete window.closeWhatsappTemplateCreator
  }

  whatsappTemplateCreatorApp.unmount()
  window['whatsAppSettingsApp'] = null
}

export async function getCalendarSettingsData(currentLocationId: string) {
  const data = await getCalendarData(currentLocationId)
  const isServicesEnabled = store.getters['calendars/isServicesEnabled']
  const services = store.getters['calendars/getAllServices']
  const isProxyUser = store.getters['auth/isProxyUser'] || false
  const isInternalUser = store.state.user?.internalUser || false

  const authToken = await getJWT(true)
  return {
    locale: data.locale,
    locationId: currentLocationId,
    userId: data?.userId,
    source: 'connections_tab',
    isFullWidth: true,
    loggedInUserInfo: data.loggedInUserInfo,
    authToken: authToken,
    isServicesEnabled: isServicesEnabled,
    services: services,
    customObjectLabels: data.customObjectLabels,
    isProxyUser: isProxyUser,
    isInternalUser: isInternalUser,
  }
}

export {
  mountCalendarSettingsApp,
  unmountCalendarSettingsApp,
  mountWhatsappTemplateCreatorApp,
  unmountWhatsappTemplateCreatorApp,
}
